// ════════════════════════════════════════════════════════════════════════
// GraphDecoration.tsx — Estructura de grafos (nodos + conexiones) que
// representa el ciclo negocio → tecnología: desde el usuario y la
// atracción de clientes hasta la infraestructura que lo sostiene.
// ════════════════════════════════════════════════════════════════════════
// Decoración estática en SVG (no requiere canvas ni animación) — se ubica
// cerca del margen izquierdo del Hero, detrás del bloque de texto (ver
// .graphDecoration en Hero.module.css). Visible en todas las resoluciones:
// en pantallas anchas se ancla a una distancia fija del centro (en el
// espacio libre junto al texto); en pantallas angostas se ancla cerca del
// borde izquierdo y se encoge, quedando parcialmente detrás del texto —
// no estorba porque es puramente decorativo (z-index:0, pointer-events:none).
//
// PATRÓN: igual que landingData.ts — los datos (nodos, conexiones, íconos)
// viven separados del render. Cada nodo tiene un "marco" rectangular sin
// borde (solo relleno tenue, sin stroke) detrás de un ícono de trazo simple
// (líneas blancas, sin relleno) referente a desarrollo, diseño de sistemas
// o negocio. El campo `scale` de cada nodo varía el tamaño del rectángulo
// y del ícono para que el grafo no se vea como una cuadrícula uniforme.
// ════════════════════════════════════════════════════════════════════════

import styles from './Hero.module.css';

type IconType =
  | 'usuario' | 'atraccion' | 'negocio' | 'ventas' | 'escalabilidad'
  | 'administracion' | 'lenguajes' | 'iac' | 'nube' | 'basedatos' | 'seguridad';

interface GraphNode {
  id: string;
  x: number;
  y: number;
  icon: IconType;
  label: string;
  /** Factor de escala del marco + ícono (1 = tamaño base) — da variedad de tamaños. */
  scale: number;
}

// Posiciones dentro de un viewBox de 220×640 — con leve desorden orgánico
// (no es una cuadrícula perfecta) para que se lea como "grafo", no como tabla.
// `scale` también varía a propósito, sin patrón regular.
const NODES: readonly GraphNode[] = [
  { id: 'usuario',        x: 110, y: 40,  icon: 'usuario',        label: 'Usuario',        scale: 1.05 },
  { id: 'atraccion',      x: 55,  y: 115, icon: 'atraccion',      label: 'Atracción',      scale: 0.82 },
  { id: 'negocio',        x: 155, y: 145, icon: 'negocio',        label: 'Negocio',        scale: 1.25 },
  { id: 'ventas',         x: 85,  y: 220, icon: 'ventas',         label: 'Ventas',         scale: 0.9 },
  { id: 'escalabilidad',  x: 165, y: 290, icon: 'escalabilidad',  label: 'Escalabilidad',  scale: 1.15 },
  { id: 'administracion', x: 50,  y: 310, icon: 'administracion', label: 'Administración', scale: 0.78 },
  { id: 'lenguajes',      x: 115, y: 390, icon: 'lenguajes',      label: 'Lenguajes de programación', scale: 1.1 },
  { id: 'iac',            x: 170, y: 450, icon: 'iac',            label: 'Infraestructura como código', scale: 0.95 },
  { id: 'nube',           x: 55,  y: 465, icon: 'nube',           label: 'Nube',           scale: 1.0 },
  { id: 'basedatos',      x: 115, y: 545, icon: 'basedatos',      label: 'Bases de datos', scale: 1.2 },
  { id: 'seguridad',      x: 170, y: 600, icon: 'seguridad',      label: 'Seguridad',      scale: 0.85 },
];

// Conexiones del grafo — no todos contra todos, solo las relaciones que
// cuentan la historia "negocio arriba, infraestructura técnica abajo".
const EDGES: ReadonlyArray<readonly [string, string]> = [
  ['usuario', 'atraccion'], ['atraccion', 'ventas'], ['usuario', 'negocio'],
  ['negocio', 'escalabilidad'], ['negocio', 'administracion'], ['ventas', 'escalabilidad'],
  ['administracion', 'lenguajes'], ['escalabilidad', 'basedatos'], ['lenguajes', 'iac'],
  ['lenguajes', 'nube'], ['iac', 'basedatos'], ['nube', 'basedatos'],
  ['basedatos', 'seguridad'], ['iac', 'seguridad'],
];

// Tamaño base del marco rectangular (a scale = 1) — cada nodo lo multiplica
// por su propio `scale` para variar de tamaño.
const NODE_W = 42;
const NODE_H = 30;

// ── Íconos — trazos simples (solo <path>/<line>/<circle>, sin relleno) ──
// Cada uno centrado en (0,0) dentro de un espacio de ~14×14.
function NodeIcon({ type }: { type: IconType }) {
  switch (type) {
    case 'usuario':
      return (
        <>
          <circle cx={0} cy={-3.5} r={3} />
          <path d="M -5.5 6.5 a 5.5 5.5 0 0 1 11 0" />
        </>
      );
    case 'atraccion': // imán — representa atracción de clientes/leads
      return (
        <>
          <path d="M -4 -6 L -4 1.5 A 4 4 0 0 0 4 1.5 L 4 -6" />
          <line x1={-4} y1={-6} x2={-1.3} y2={-6} />
          <line x1={4} y1={-6} x2={1.3} y2={-6} />
          <line x1={-4} y1={-3.2} x2={-1.3} y2={-3.2} />
          <line x1={4} y1={-3.2} x2={1.3} y2={-3.2} />
        </>
      );
    case 'negocio': // maletín
      return (
        <>
          <rect x={-6} y={-2} width={12} height={8} rx={1} />
          <path d="M -2.5 -2 v-1.5 a 1 1 0 0 1 1 -1 h 3 a 1 1 0 0 1 1 1 v 1.5" />
          <line x1={-6} y1={1.5} x2={6} y2={1.5} />
        </>
      );
    case 'ventas': // etiqueta de precio
      return (
        <>
          <path d="M -1 -6 L 5.5 -6 L 5.5 0.5 L -1 6 L -6 1 Z" />
          <circle cx={2} cy={-3.3} r={0.9} />
        </>
      );
    case 'escalabilidad': // barras ascendentes
      return (
        <>
          <line x1={-5} y1={6} x2={-5} y2={1} />
          <line x1={0} y1={6} x2={0} y2={-3} />
          <line x1={5} y1={6} x2={5} y2={-6} />
        </>
      );
    case 'administracion': // engranaje simplificado
      return (
        <>
          <circle cx={0} cy={0} r={3.2} />
          {[0, 60, 120, 180, 240, 300].map((deg) => {
            const rad = (deg * Math.PI) / 180;
            return (
              <line
                key={deg}
                x1={Math.cos(rad) * 4.2}
                y1={Math.sin(rad) * 4.2}
                x2={Math.cos(rad) * 6}
                y2={Math.sin(rad) * 6}
              />
            );
          })}
        </>
      );
    case 'lenguajes': // </>
      return (
        <>
          <path d="M -3 -6 L -7 0 L -3 6" />
          <path d="M 3 -6 L 7 0 L 3 6" />
        </>
      );
    case 'iac': // servidores apilados — infraestructura provisionada por código
      return (
        <>
          {[-5, 0, 5].map((y) => (
            <g key={y}>
              <rect x={-6} y={y - 2} width={12} height={3.4} rx={0.6} />
              <circle cx={3.7} cy={y - 0.3} r={0.5} />
            </g>
          ))}
        </>
      );
    case 'nube':
      return (
        <path d="M -6 3 a 3.2 3.2 0 0 1 0.4 -6.4 a 4 4 0 0 1 7.6 -1 a 3.4 3.4 0 0 1 1.5 6.9 a 1 1 0 0 1 -0.3 0.5 Z" />
      );
    case 'basedatos':
      return (
        <>
          <ellipse cx={0} cy={-4.5} rx={5.5} ry={2} />
          <line x1={-5.5} y1={-4.5} x2={-5.5} y2={4.5} />
          <line x1={5.5} y1={-4.5} x2={5.5} y2={4.5} />
          <path d="M -5.5 4.5 a 5.5 2 0 0 0 11 0" />
        </>
      );
    case 'seguridad': // escudo con check
      return (
        <>
          <path d="M 0 -6.5 L 5.5 -4.5 V 1 A 5.5 7 0 0 1 0 6.5 A 5.5 7 0 0 1 -5.5 1 V -4.5 Z" />
          <path d="M -2.3 0 L -0.5 2 L 2.6 -2" />
        </>
      );
    default:
      return null;
  }
}

// Acorta una línea en sus dos extremos para que no entre debajo de los
// marcos ni tape la punta de flecha — deja un espacio de "aire" alrededor
// de cada nodo, igual que en el diagrama de referencia.
function shortenLine(
  ax: number, ay: number, bx: number, by: number,
  startGap: number, endGap: number,
) {
  const dx = bx - ax;
  const dy = by - ay;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  return {
    x1: ax + ux * startGap,
    y1: ay + uy * startGap,
    x2: bx - ux * endGap,
    y2: by - uy * endGap,
  };
}

// Radio aproximado del marco de un nodo (mitad de su diagonal mayor) más un
// margen de aire — como los marcos ahora varían de tamaño (`scale`), el
// espacio que deja la línea antes de llegar al nodo varía con él en vez de
// ser un valor fijo.
function nodeGap(n: GraphNode, extra = 0) {
  return (Math.max(NODE_W, NODE_H) * n.scale) / 2 + 6 + extra;
}

interface GraphDecorationProps {
  /** Cuando es true, dispara la animación de entrada (fade + slide-up) —
   * pásalo desde el mismo `isVisible` del IntersectionObserver que usa
   * .content en Hero.tsx, para que todo el Hero entre sincronizado. */
  visible?: boolean;
}

export default function GraphDecoration({ visible = false }: GraphDecorationProps) {
  const nodeById = Object.fromEntries(NODES.map((n) => [n.id, n]));

  return (
    <svg
      className={`${styles.graphDecoration} ${visible ? styles.graphVisible : ''}`}
      viewBox="0 0 220 640"
      role="img"
      aria-label="Diagrama de conexión entre negocio y tecnología"
      aria-hidden="true"
    >
      <defs>
        {/* Punta de flecha — mismo blanco tenue que las líneas */}
        <marker
          id="graphArrow"
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 Z" fill="rgba(255,255,255,0.4)" />
        </marker>
      </defs>

      {/* Conexiones — se dibujan primero, quedan detrás de los íconos.
          Se acortan en ambos extremos para dejar aire junto a cada nodo
          y que la punta de flecha se vea completa, sin encimarse con el ícono. */}
      <g stroke="rgba(255,255,255,0.22)" strokeWidth={1}>
        {EDGES.map(([a, b]) => {
          const na = nodeById[a];
          const nb = nodeById[b];
          const { x1, y1, x2, y2 } = shortenLine(
            na.x, na.y, nb.x, nb.y,
            nodeGap(na), nodeGap(nb, 6),
          );
          return (
            <line
              key={`${a}-${b}`}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              markerEnd="url(#graphArrow)"
            />
          );
        })}
      </g>

      {/* Nodos: marco rectangular sin borde (solo relleno tenue, sin stroke)
          detrás del ícono. El tamaño de ambos varía con `n.scale` para que
          el grafo no se lea como una cuadrícula uniforme. */}
      {NODES.map((n) => {
        const w = NODE_W * n.scale;
        const h = NODE_H * n.scale;
        return (
          <g key={n.id} transform={`translate(${n.x},${n.y})`}>
            <rect
              x={-w / 2}
              y={-h / 2}
              width={w}
              height={h}
              rx={5 * n.scale}
              fill="rgba(255,255,255,0.05)"
            />
            <g
              transform={`scale(${n.scale})`}
              stroke="rgba(255,255,255,0.75)"
              strokeWidth={1.4}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <NodeIcon type={n.icon} />
            </g>
          </g>
        );
      })}
    </svg>
  );
}
